import { defineStore } from 'pinia';
import type {
	ErrorResponse,
	QuestionItem,
	AnswersSend,
	QuestionItemSend,
} from '@/types';
import API from '@/api';
import type { AxiosError } from 'axios';
import { setLocale, loadMessagesFor } from '@/i18n';
import * as SentryVue from '@sentry/vue';

interface SurveyState {
	cid: string;
	language: string;
	questions: QuestionItem[];
	answers: AnswersSend;
	currentQuestion: QuestionItemSend;
	ignoredQuestions: string[];
	isCompleted: boolean;
	valid: boolean;
	loading: boolean;
	currentStep: number;
	totalSteps: number;
	showPreloader: boolean;
	error: ErrorResponse | boolean;
	validationFail: boolean;
	errorMessage: string;
	themeListener: Function | null;
	enableColorsSwitch: boolean;
}

export const useSurveyStore = defineStore('survey', {
	state: (): SurveyState => ({
		cid: '',
		language: 'en',
		questions: [],
		answers: {
			cid: '',
			questions: [],
		},
		currentQuestion: {
			id: '',
			delay_time: 0,
			answers: [],
		},
		ignoredQuestions: [],
		isCompleted: false,
		loading: false,
		valid: false,
		showPreloader: false,
		error: false,
		totalSteps: 0,
		currentStep: 1,
		errorMessage: '',
		validationFail: false,
		themeListener: null as ((e: MediaQueryListEvent) => void) | null,
		enableColorsSwitch: false,
	}),
	getters: {
		getProgress: ({ currentStep, totalSteps, valid }): number => {
			if (valid) {
				return (currentStep / totalSteps) * 100;
			}

			return ((currentStep - 1) / totalSteps) * 100;
		},
		isValidationError: (state): boolean => state.validationFail,
		getErrorMessage: (state): string => state.errorMessage,
		isColorsSwitchEnabled: (state): boolean => state.enableColorsSwitch,
	},
	actions: {
		async init() {
			const url = new URL(window.location.href);
			const cid = url.searchParams.get('cid');
			const appId = url.searchParams.get('ah');
			const language = url.searchParams.get('language');
			const country = url.searchParams.get('country');
			API.defaults.headers.common['X-User-Locale'] = language;

			if (cid !== null && cid !== '') {
				this.cid = cid;
				SentryVue.setTag('cid', this.cid);
			} else {
				this.error = true;
				this.logSentryError('Survey initialization failed: cid is missing');
			}

			if (appId !== null && appId !== '') {
				SentryVue.setTag('app_id', appId);
			}

			if (
				language !== null &&
				language !== '' &&
				country !== null &&
				country !== ''
			) {
				this.language = ['pt_br', 'pt-br'].includes(language.toLowerCase())
					? 'pt'
					: language;
				const locale = `${this.language.toLowerCase()}-${country.toLowerCase()}`;
				await loadMessagesFor(locale);
				setLocale(locale);
			}

			const appsDynamicColorsScheme = import.meta.env.VITE_PS_EXMOX_APP_IDS
				? import.meta.env.VITE_PS_EXMOX_APP_IDS.split(';')
				: [];

			if (appsDynamicColorsScheme.includes(appId)) {
				this.enableColorsSwitch = true;
			}
		},
		async getQuestions() {
			try {
				this.error = false;
				this.showPreloader = true;
				this.questions = [];

				const { data } = await API.get(
					`/external-survey/questions?cid=${this.cid}`
				);

				if (data?.popular_questions) {
					this.questions.push(...data['popular_questions']);
				}

				if (data?.system_question) {
					this.questions.push(data['system_question']);
				}
				this.getTotalSteps();
			} catch (error) {
				this.error =
					((error as AxiosError).response?.data as ErrorResponse) || true;
				console.error('Error when get questions:', error);
				this.logSentryError(
					'Error when get questions',
					this.error?.message,
					error as AxiosError
				);
			} finally {
				this.showPreloader = false;
			}
		},
		async sendAnswers() {
			try {
				this.resetErrorState();
				this.loading = true;
				this.answers.cid = this.cid;
				const SURVEY_PANEL_BASE_URL =
					import.meta.env.VITE_ROUTER_BASE_URL + '/in';

				await API.post('/external-survey/answers', this.answers);

				this.isCompleted = true;
				setTimeout(() => {
					document.location.href = `${SURVEY_PANEL_BASE_URL}/?ident=${this.cid}&sup=1&result=1`;
				}, 2000);
			} catch (error) {
				error = error as AxiosError;
				this.error =
					((error as AxiosError).response?.data as ErrorResponse) || true;
				console.error('Error when post answers:', error);
				this.errorMessage = this.error?.message;
				this.validationFail = (error as AxiosError).response?.status === 422;

				this.logSentryError(
					'Error when post answers',
					this.errorMessage,
					error as AxiosError
				);
			} finally {
				this.loading = false;
			}
		},
		getTotalSteps(): void {
			this.totalSteps = this.questions.length;
		},
		setNextStep(): void {
			this.currentStep++;
			this.setValidationStatus(false);
		},
		setValidationStatus(payload: boolean): void {
			this.valid = payload;
		},
		setAnswers(): void {
			const answerIndex = this.answers.questions.findIndex(
				(q) => q.id === this.currentQuestion.id
			);
			if (answerIndex !== -1) {
				this.answers.questions[answerIndex] = { ...this.currentQuestion };
				return;
			} else {
				this.answers.questions.push({ ...this.currentQuestion });
			}
		},
		setCurrentQuestion({ id, delay_time, answers }: QuestionItemSend): void {
			if (id) {
				this.currentQuestion.id = id;
			}
			if (delay_time) {
				this.currentQuestion.delay_time = delay_time;
			}
			if (answers) {
				this.currentQuestion.answers = answers;
			}
		},
		setIgnoreQuestions(ignoredQuestions: string[]): void {
			this.ignoredQuestions = ignoredQuestions;
			this.getTotalSteps();

			if (ignoredQuestions.length && this.currentStep < this.totalSteps) {
				this.totalSteps -= this.ignoredQuestions.length;
			}
		},
		excludeQuestions(): void {
			if (this.ignoredQuestions.length) {
				this.questions = this.questions.filter(({ id }) => {
					return this.ignoredQuestions.every((questionId) => {
						return questionId !== id;
					});
				});
			}

			this.getTotalSteps();
		},
		resetErrorState() {
			this.error = false;
			this.validationFail = false;
			this.errorMessage = '';
		},
		logSentryError(
			errorMessage: string,
			message?: string,
			errorDetails?: AxiosError
		): void {
			SentryVue.captureException(new Error(errorMessage), {
				level: 'error',
				extra: {
					location: window.location.href,
					cid: this.cid,
					message: message || '',
					...(errorDetails && errorDetails?.config
						? {
								configMethod: errorDetails?.config?.method,
								configData: errorDetails?.config?.data,
								configUrl: errorDetails?.config?.url,
						  }
						: {}),
					...(errorDetails && errorDetails?.response
						? {
								responseStatus: errorDetails?.response?.status,
								responseHeaders: errorDetails?.response?.headers,
								responseData: errorDetails?.response?.data,
								responseMessage: errorDetails?.response?.data?.message,
						  }
						: {}),
					headers: API.defaults.headers.common,
					appStore: this.$state,
					answers: JSON.stringify(this.answers),
					currentQuestion: JSON.stringify(this.currentQuestion),
				},
			});
		},
		setColorScheme(colorScheme: 'dark' | 'light') {
			document.documentElement.setAttribute('data-theme', colorScheme);
		},
		checkInitialTheme() {
			const isDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
			this.setColorScheme(isDark ? 'dark' : 'light');
		},
		listenToAppTheme() {
			// keep the listener reference so we can remove it later
			this.themeListener = (e: MediaQueryListEvent) => {
				this.setColorScheme(e.matches ? 'dark' : 'light');
			};

			window
				.matchMedia('(prefers-color-scheme: dark)')
				.addEventListener(
					'change',
					this.themeListener as (e: MediaQueryListEvent) => void
				);
		},
		stopListening() {
			if (this.themeListener) {
				window
					.matchMedia('(prefers-color-scheme: dark)')
					.removeEventListener(
						'change',
						this.themeListener as (e: MediaQueryListEvent) => void
					);
				this.themeListener = null;
			}
		},
	},
});
